import { useGetDiagnosisCategoriesV2Query } from '@/hooks/telemedicine/telemedicine.gql.impl';
import {
  DepartmentCategoryModel,
  DiagnosisCategoryInfoModel,
  PopularDepartmentCategoryModel,
  PopularTreatmentCategoryModel,
  TreatmentCategoryModel,
} from '@/models/diagnosis/category';
import { DiagnosisContactType, GetDiagnosisCategoriesV2Query } from '@/types/bff/gql-schema';
import { QueryClient } from '@tanstack/react-query';

const parseDiagnosisCategories = (data: GetDiagnosisCategoriesV2Query) => {
  const categoriesInfo = data.getDiagnosisCategoriesV2;
  return new DiagnosisCategoryInfoModel(
    categoriesInfo.popularDepartmentCategories.map((departmentCategory) => {
      return new PopularDepartmentCategoryModel(departmentCategory.id, departmentCategory.name);
    }),
    categoriesInfo.popularTreatmentCategories.map((treatmentCategory) => {
      return new PopularTreatmentCategoryModel(treatmentCategory.id, treatmentCategory.name);
    }),
    categoriesInfo.departmentCategories.map((departmentCategory) => {
      return new DepartmentCategoryModel(
        departmentCategory.id,
        departmentCategory.name,
        departmentCategory.description ?? '',
        departmentCategory.iconUrl
      );
    }),
    categoriesInfo.treatmentCategories.map((treatmentCategory) => {
      return new TreatmentCategoryModel(
        treatmentCategory.id,
        treatmentCategory.name,
        treatmentCategory.iconUrl,
        treatmentCategory.items.map((treatment) => {
          return {
            id: treatment.id,
            name: treatment.name,
          };
        })
      );
    })
  );
};

const useGetDiagnosisCategories = () => {
  const { data: categoriesInfo = new DiagnosisCategoryInfoModel([], [], [], []) } = useGetDiagnosisCategoriesV2Query(
    {
      contactType: DiagnosisContactType.NonF2F,
    },
    {
      select: parseDiagnosisCategories,
    }
  );
  return {
    categoriesInfo,
  };
};

export default useGetDiagnosisCategories;

useGetDiagnosisCategories.getKey = () => {
  return useGetDiagnosisCategoriesV2Query.getKey({
    contactType: DiagnosisContactType.NonF2F,
  });
};

useGetDiagnosisCategories.prefetchQuery = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery(
    useGetDiagnosisCategories.getKey(),
    useGetDiagnosisCategoriesV2Query.fetcher({
      contactType: DiagnosisContactType.NonF2F,
    })
  );
};

useGetDiagnosisCategories.fetchQuery = async (queryClient: QueryClient) => {
  const data = await queryClient.fetchQuery(
    useGetDiagnosisCategories.getKey(),
    useGetDiagnosisCategoriesV2Query.fetcher({
      contactType: DiagnosisContactType.NonF2F,
    })
  );

  return {
    categoriesInfo: parseDiagnosisCategories(data),
  };
};
